import { capitalize, isEmpty, upperCase } from 'lodash-es'
import { useGetInfoRequired } from 'hooks/queries/useGetInfoRequiredQuery'
import { EMBEDDED_CUSTOMER_STATUSES } from '../helpers/constants'
import {
  AssociateRequirement,
  EmbeddedCustomerAssociate,
  InfoRequired,
  UnifiedAssociate,
} from '../helpers/types'

export const useAssociateDetails = (
  reference: string,
  associatesData?: EmbeddedCustomerAssociate[]
) => {
  const { data, isLoading, isError, refetch } = useGetInfoRequired(reference)
  const infoRequired: InfoRequired['infoRequired'] = data?.infoRequired
  const status: InfoRequired['status'] = data?.status
  const pendingAssociatesRaw: AssociateRequirement[] = infoRequired?.associates || []

  const mapVerifiedAssociates = (verified?: EmbeddedCustomerAssociate[]): UnifiedAssociate[] => {
    if (!verified?.length) return []
    return verified.map(v => ({
      id: `${v?.person?.firstName ?? ''}-${v?.person?.lastName ?? ''}`.trim(),
      title: capitalize(upperCase(v?.title ?? 'Associate')),
      body: `${v?.person?.firstName ?? ''} ${v?.person?.lastName ?? ''}`.trim(),
      status: EMBEDDED_CUSTOMER_STATUSES.VERIFIED,
    }))
  }

  const mapPendingIdentificationUploadAssociates = (
    pending: AssociateRequirement[]
  ): UnifiedAssociate[] => {
    if (!pending?.length) return []
    return pending
      .filter(p => p?.associateName)
      .map(p => {
        const primaryRole: string | undefined = Array.isArray(p?.roles) ? p.roles[0] : undefined
        return {
          id: `${p?.associateName ?? ''}-${primaryRole ?? 'PENDING'}`.trim(),
          title: capitalize(upperCase(primaryRole ?? 'Associate')),
          body: String(p?.associateName ?? ''),
          status: EMBEDDED_CUSTOMER_STATUSES.PENDING,
        }
      })
  }

  const pendingVerificationStatus =
    isEmpty(infoRequired) &&
    status !== EMBEDDED_CUSTOMER_STATUSES.VERIFIED &&
    infoRequired?.associates?.length

  const unifiedAssociates: UnifiedAssociate[] = [
    ...mapPendingIdentificationUploadAssociates(pendingAssociatesRaw),
    ...mapVerifiedAssociates(associatesData),
  ]

  const requiresAssociates = !!infoRequired?.associates?.find(a => a.roles)?.roles?.length

  return {
    associates: unifiedAssociates,
    isLoading,
    isError,
    refetch,
    pendingVerificationStatus,
    requiresAssociates,
  }
}
